import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfig } from 'src/app/configs/app.config';
import { EventType } from 'src/app/constants/events-type';
import { AuthFactoryService } from 'src/app/core/services/auth/auth-factory.service';
import { EventManagerService } from 'src/app/core/services/event-manager/event-manager.service';
import { EventContent } from 'src/app/core/services/event-manager/models/event-content';
import { AuthenticatedUserService } from 'src/app/core/services/http/authenticated-user/authenticated-user.service';
import { School } from 'src/app/core/services/http/school/interfaces/school';
import { Alert } from 'src/app/shared/alert/types/alert';
import { MembershipService } from 'src/app/core/services/http/membership/membership.service';
import { AuthUserService } from 'src/app/core/services/auth/auth-user.service';
import { LocalStorageService } from 'src/app/core/services/local-storage/local-storage.service';
import { SchoolService } from 'src/app/core/services/http/school/school.service';

@Injectable({ providedIn: 'root' })
export class LoginService {
  public onSchoolSelected: EventEmitter<any> = new EventEmitter<any>();
  constructor(
    private router: Router,
    private eventManager: EventManagerService,
    public authenticatedUserService: AuthenticatedUserService,
    public authFactoryService: AuthFactoryService,
    private activatedRoute: ActivatedRoute,
    public membershipService: MembershipService,
    public authUserService: AuthUserService,
    public schoolService: SchoolService,
    private localStorageService: LocalStorageService
  ) {}

  async chooseSchool(school: School, setAsDefault = false): Promise<void> {
    this.authFactoryService.service.setSchoolUserAuthority(school);
    
    const ftmSchool = await this.schoolService.getSchool().toPromise();
    if(!ftmSchool){
      return;
    }
    this.authUserService.setIsAutoAssignEnabledSchool(ftmSchool.isAutoAssignEnabledSchool, ftmSchool.isAutoAssignEnabledSchoolYear);


    this.onSchoolSelected.emit(school);

    if (setAsDefault) {
      this.membershipService.changeDefaultSchool(
        school.membership.uuid
      )
      .subscribe(
        () => null,
        () => this.onError()
      );
    }

    this.schoolService.getSchool().subscribe((ftmSchool: School) => {
      this.localStorageService.setItem('isAutoAssignEnabledSchool', ftmSchool.isAutoAssignEnabledSchool);
    });
    
    const defaultRoute = AppConfig.DefaultRouteUserRoleMapping[school.role];
    if (defaultRoute) {
      this.router.navigateByUrl(defaultRoute);
    }
  }

  async automaticallyChooseSchool(schools: School[]): Promise<boolean> {
    const defaultSchool = schools.find(i => i?.membership?.isDefault === 1);

    if (defaultSchool) {
      this.chooseSchool(defaultSchool);
    } else if (schools.length === 1) {
      this.chooseSchool(schools[0]);
    } else {
      return false;
    }

    return true;
  }

  onError(): void {
    const errorMessage = this.activatedRoute.snapshot.queryParams.errorMessage;

    if (errorMessage) {
      const alert: Alert = {
        type: 'danger',
        dismissOnTimeout: 0,
        translateKey: errorMessage
      };

      this.authFactoryService.service.removeVerifierCode();
      this.eventManager.broadcast(new EventContent(EventType.AlertMessage, alert));
    }
  }

  redirectToProviderUrl(url: string): void {
    if (url.length > 0) {
      window.location.href = url;
    }
  }

  isSchoolExistInSchoolList(school: School, schools: School[]): boolean {
    return schools.find(item => item.uuid === school.uuid && item.role === school.role) !== undefined;
  }

  async isUserEqualToLastLoggedInUser(): Promise<boolean> {
    const lastAuthenticatedUser = this.authFactoryService.service.getAuthenticatedUser();
    const authenticatedUser = await this.authenticatedUserService.getAuthenticatedUser().toPromise();

    if(!authenticatedUser){
      return false;
    }
    return authenticatedUser.user.uuid === lastAuthenticatedUser?.body?.user.uuid;
  }
}
